/**
 * Build a Hostelworld affiliate clickout URL.
 *
 * The legacy `findabed.php/ChosenCity.../ChosenCountry...` pattern is dead and
 * 404s on every destination. The current canonical Hostelworld URL is
 * `/hostels/{region}/{country-slug}/{city-slug}/` — but `/hostels/{city-slug}`
 * (without region/country) reliably 302-redirects to the canonical URL.
 *
 * The full chain:
 *   click  → hostelworld.prf.hn (affiliate camref tracking)
 *          → hostelworld.com/hostels/{city-slug}
 *          → hostelworld.com/hostels/{region}/{country}/{city}/  (302)
 *
 * Always pass the destination's city — never the user's profile country.
 */
const HW_AFFILIATE_PREFIX = "https://hostelworld.prf.hn/click/camref:1100ldS3s/[p_id:1011l175487]/destination:";

const slugify = (input: string): string =>
	input
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "") // strip diacritics (São → Sao)
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-") // non-alphanumerics → single hyphen
		.replace(/^-+|-+$/g, ""); // trim leading/trailing hyphens

export const buildHostelworldUrl = (city: string | undefined | null, _country?: string | null): string => {
	const slug = slugify((city || "").trim());
	const target = slug ? `https://www.hostelworld.com/hostels/${slug}` : "https://www.hostelworld.com/";
	return `${HW_AFFILIATE_PREFIX}${encodeURIComponent(target)}`;
};

/**
 * Wrap an ALREADY-RESOLVED Hostelworld URL (e.g. a stored place link, rather than a city name we'd
 * build a search URL for) in the same Partnerize click-tracking prefix `buildHostelworldUrl` uses.
 * Used by `stampAffiliateUrl` (urlUtils.ts) so a stored hostelworld.com URL gets our camref instead
 * of opening untracked.
 */
export const wrapHostelworldUrl = (url: string): string => `${HW_AFFILIATE_PREFIX}${encodeURIComponent(url)}`;
